import { baseApi } from "../baseApi";
import type { DataGridPagination } from "@/sharedTypes";

const auditLogTag = "AuditLog";

export interface AuditLogEntry {
  id: number;
  company_id: number | null;
  actor_agent_id: number | null;
  actor_username: string | null;
  actor_display_name: string | null;
  actor_role: number | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  entity_label: string | null;
  changes: {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    meta?: Record<string, unknown>;
  } | null;
  ip: string | null;
  user_agent: string | null;
  createdAt: string;
}

export interface AuditLogQuery {
  page?: number;
  pageSize?: number;
  entity_type?: string;
  action?: string;
  actor_agent_id?: number;
  entity_id?: string;
  search?: string;
  from?: string;
  to?: string;
  /** Super admin only: scope to one company. */
  company_id?: number;
}

export interface AuditLogResponse {
  items: AuditLogEntry[];
  pagination: DataGridPagination;
}

export interface AuditLogFilters {
  entity_types: string[];
  actions: string[];
}

const clean = (params: AuditLogQuery): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = String(v);
  });
  return out;
};

/** Where a single record of each audited type can be fetched from. */
const RECORD_URLS: Record<string, (id: string) => string> = {
  agent: (id) => `/agents/${id}`,
  group: (id) => `/groups/${id}`,
  client: (id) => `/clients/${id}`,
  client_group: (id) => `/client-groups/${id}`,
  shift_hours: (id) => `/shift-hours/${id}`,
  company: (id) => `/company/${id}`,
  ticket: (id) => `/gw/tickets/${id}`,
  category: (id) => `/gw/categories/${id}`,
  subcategory: (id) => `/gw/subcategories/${id}`,
  category_type: (id) => `/gw/category-types/${id}`,
  update_reason: (id) => `/gw/update-reasons?update_reason_id=${id}`,
  priority: (id) => `/gw/priorities/${id}`,
  status: (id) => `/gw/status/${id}`,
  kb_issue: (id) => `/gw/kb-issues/${id}`,
  kb_solution: (id) => `/gw/kb-solutions/${id}`,
};

export const canFetchRecord = (entityType: string): boolean => entityType in RECORD_URLS;

/** Responses come in several shapes: {data}, {items:[…]}, or the bare row. */
const unwrapRecord = (response: any): Record<string, unknown> | null => {
  if (!response || typeof response !== "object") return null;
  if (Array.isArray(response.items)) return response.items[0] ?? null;
  if (response.data && typeof response.data === "object") {
    return Array.isArray(response.data) ? response.data[0] ?? null : response.data;
  }
  return response;
};

export const auditLogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAuditRecord: builder.query<
      Record<string, unknown> | null,
      { entity_type: string; id: string | number }
    >({
      query: ({ entity_type, id }) => RECORD_URLS[entity_type]?.(String(id)) ?? "",
      transformResponse: (response: any) => unwrapRecord(response),
    }),
    getAuditLogs: builder.query<AuditLogResponse, AuditLogQuery>({
      query: (params) => ({ url: `/audit-logs`, params: clean(params) }),
      providesTags: [auditLogTag],
    }),
    getAuditLogFilters: builder.query<AuditLogFilters, void>({
      query: () => `/audit-logs/filters`,
      providesTags: [auditLogTag],
    }),
  }),
});

export const {
  useGetAuditLogsQuery,
  useGetAuditLogFiltersQuery,
  useGetAuditRecordQuery,
} = auditLogApi;
